import { AuthApiTokenResponse, authenticateWithApiToken } from "#next/pages/api/auth.action";
import { getCookieKey } from "#/lib/supabase/utils";
import { isJwtExpired } from "#/utils/jwt";

export class ApiKeySessionManager {
  private isAuthenticating = false;
  private constructor(
    private apiToken: string,
    private userSession: AuthApiTokenResponse["user"]
  ) {}

  private static async getUserSession(
    apiToken: string
  ): Promise<AuthApiTokenResponse["user"]> {
    let userSession: AuthApiTokenResponse;
    try {
      userSession = await authenticateWithApiToken({
        apiToken: apiToken
      });
    } catch (e) {
      if (process.env.VERCEL_ENV === "development") {
        console.error("Error authenticating with apiToken, is the next server running?");
      }
      throw e;
    }
    const accessToken: string = userSession.user.accessToken ?? null;
    const userId = userSession.user.id ?? null;
    const jwt = userSession.user.jwt ?? null;

    if (!accessToken || !userId || !jwt) {
      throw new Error("Error: Failed to authenticate with apiToken");
    }

    return userSession.user;
  }

  async getSessionHeaders(): Promise<Record<string, string> | null> {
    if (isJwtExpired(this.userSession.accessToken)) {
      if (this.isAuthenticating) {
        return null; // Prevent multiple requests for the same session
      }
      try {
        this.isAuthenticating = true;
        this.userSession = await ApiKeySessionManager.getUserSession(this.apiToken);
      } finally {
        this.isAuthenticating = false;
      }
    }
    return {
      Authorization: `Bearer ${this.userSession.accessToken}`,
      "user-id": this.userSession.id,
      Cookie: `${getCookieKey()}=${this.userSession.jwt}`
    };
  }

  get user() {
    return this.userSession;
  }

  static async new(
    apiToken: string
  ): Promise<ApiKeySessionManager> {
    const userSession = await ApiKeySessionManager.getUserSession(apiToken);
    const manager = new ApiKeySessionManager(apiToken, userSession);
    return manager;
  }
}